import React, { useEffect, useState } from 'react';
import { X, Vault } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/api';

export default function ImportPortfolioDialog({ program, count, onClose, onDone }) {
  const [funds, setFunds] = useState([]);
  const [fundId, setFundId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    api.get('/vc/managed-funds').then(({ data }) => {
      if (!active) return;
      setFunds(data);
      if (data.some(f => f.id === program.converted_managed_fund_id)) setFundId(program.converted_managed_fund_id);
    }).catch(e => { if (active) { setLoadFailed(true); setError(e.response?.data?.detail || 'Could not load funds. Please reopen this dialog.'); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [program.converted_managed_fund_id]);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  const submit = async e => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const { data } = await api.post(`/vc/programs/${program.id}/convert-to-portfolio`, { managed_fund_id: fundId || null });
      toast.success(`${data.moved} added to ${data.managed_fund_name}${data.skipped ? ` · ${data.skipped} already linked or unavailable` : ''}`);
      if (data.needs_review) toast.warning(`${data.needs_review} application(s) need an identity review and were not added.`);
      onDone();
    } catch (e) { setError(e.response?.data?.detail || 'Unable to add these startups. Please try again.'); }
    finally { setBusy(false); }
  };
  return <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
    <section role="dialog" aria-modal="true" aria-labelledby="portfolio-dialog-title" className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-4"><Vault size={25} className="text-primary" /><button type="button" onClick={onClose} disabled={busy} aria-label="Close portfolio dialog" className="p-2 rounded-lg hover:bg-background"><X size={20} /></button></div>
      <h2 id="portfolio-dialog-title" className="font-heading text-xl font-bold text-text-primary">Add accepted startups to portfolio</h2>
      <p className="mt-3 text-sm text-text-secondary">{count} accepted application{count === 1 ? '' : 's'} from {program.name}. Existing profiles and memberships are reused.</p>
      <form onSubmit={submit} className="mt-5 space-y-4">
        <label htmlFor="portfolio-fund" className="block text-sm font-semibold text-text-primary">Portfolio fund</label>
        <select id="portfolio-fund" autoFocus value={fundId} onChange={e => setFundId(e.target.value)} disabled={loading || busy} className="w-full rounded-lg border border-border bg-background text-text-primary px-3 py-3">
          <option value="">Create / reuse a fund named {program.name}</option>
          {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <p className="text-xs text-text-muted">Only accepted applicants are added. Their program membership and uploaded answers stay intact. This action does not send emails or create login accounts.</p>
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-3 pt-3"><button type="button" disabled={busy} onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-text-primary">Cancel</button><button disabled={loading || busy || loadFailed} className="px-4 py-2 rounded-lg bg-primary text-white font-semibold disabled:opacity-50">{busy ? 'Adding…' : 'Add to portfolio'}</button></div>
      </form>
    </section>
  </div>;
}
